/**
 * Reseller Billing tab (TMMPDM-0037, tmm-api 0098): the reseller's card on file, read-only
 * (OBJ/FRM-015), and its charges — minimum shortfalls and statement deficits — with **Retry** on a
 * retryable one. Retry charges money, so it confirms naming the amount and the card (ACT-004/007),
 * and one in-flight guard stops a double submit (ACT-008). Every retry, success or not, re-reads.
 * `clientAction` on the card carries a Stripe secret: it is never typed, read or rendered.
 * `?charge=<id>` (from a Standing month or a charged settlement) marks that charge's row.
 * TMMPDM-0044: the linked row scrolls into view, its first cell takes focus, and it says "Linked
 * charge" to assistive technology and with a bar, not by the tint alone (STATE-002 / A11Y-010).
 * TMMPDM-0045 (tmm-api 0109): Retry is offered on the server's `retryable` alone, whatever the
 * status (a `REQUIRES_ACTION` charge the bank wants confirmed included); a 409
 * `RESELLER_CHARGE_NOT_RETRYABLE` says it can't be retried now and re-reads.
 * TMMPDM-0088: the partner page splits it in two leaves — `view="charges"` (Reports › Charges, the
 * table and its retry confirm) and `view="card"` (Settings › Card on file). Both keep the one
 * card-and-charges read, because the retry confirm names the card.
 * TMMPDM-0095 / 0096: when the old payout actions are retired the charges are frozen history: a banner
 * links the partner's Ledger and Retry is gone; a stale page's retry answering 410
 * `PAYOUTS_MOVED_TO_LEDGER` says so in the confirm and re-reads. Before that, nothing changes. The
 * card on file is not payout history.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { ConfirmDialog, EmptyState, ErrorState, SubmitButton, useNotify } from '@/components/page';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Amount } from '@/components/ui';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy, isPayoutsMovedToLedger } from '@/api/errors';
import { formatInstant, formatMoney, formatMonthYear } from '@/format';
import type {
  ResellerBillingCard,
  ResellerBillingCardStatus,
  ResellerCharge,
} from '@/api/tmm/types';
import { ResellerChargeChip } from './ResellerChargeChip';
import { CHARGE_KIND_FALLBACK } from './settlementFormat';
import { StatusChip } from '@/status';
import { FrozenHistoryBanner, MovedToLedgerMessage } from '../ledger/ledgerShared';
import { resellerLedgerPath, usePayoutActionsRetired } from '../ledger/ledger';

interface ResellerBillingPanelProps {
  resellerId: number;
  /** Which block this leaf shows: the charges (with Retry) or the card on file. */
  view: 'charges' | 'card';
}

interface BillingData {
  card: ResellerBillingCard | null;
  charges: ResellerCharge[];
}

const CARD_STATUS_FALLBACK: Record<ResellerBillingCardStatus, string> = {
  ACTIVE: 'Active',
  EXPIRED: 'Card expired',
  requires_action: "Needs the reseller's confirmation",
};

const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  p: 0,
  m: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

/** The linked charge's bar: a shape as well as the selected tint. */
const LINKED_CELL_SX: SxProps<Theme> = {
  boxShadow: (th) => `inset 3px 0 0 ${(th.vars ?? th).palette.primary.main}`,
};

/** `failureCode` values tmm-api writes itself (ResellerChargeService); pay-api's pass through raw. */
const FAILURE_FALLBACK: Record<string, string> = {
  NO_CARD: 'No card on file',
  REVERIFY_CARD: 'The bank wants the card confirmed; the reseller adds it again',
  PSP_ERROR: 'The payment provider had an error',
  FAILED: 'Declined',
};

export function ResellerBillingPanel({ resellerId, view }: ResellerBillingPanelProps) {
  const { t } = useCopy();
  const notify = useNotify();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [searchParams] = useSearchParams();
  const highlighted = Number(searchParams.get('charge'));
  const [data, setData] = useState<BillingData | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [confirming, setConfirming] = useState<ResellerCharge | null>(null);
  const [retrying, setRetrying] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  // A refused retry is said inside the open confirm (TMM-B4); an answered-but-failed attempt is `problem`.
  const [confirmError, setConfirmError] = useState<ReactNode>(null);
  const payoutActions = usePayoutActionsRetired();
  const frozen = payoutActions.retired;
  const inFlight = useRef(false);
  // The linked charge's first cell, focused once per link (a re-read after a retry doesn't move focus).
  const linkedCell = useRef<HTMLTableCellElement | null>(null);
  const focusedFor = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      adminApi.getResellerBillingCard(resellerId),
      adminApi.listResellerCharges(resellerId),
    ])
      .then(([card, list]) => {
        if (cancelled) return;
        setData({ card, charges: list.charges });
        setFailure(null);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setData(null);
        setFailure(error);
      });
    return () => {
      cancelled = true;
    };
  }, [resellerId, reloadToken]);

  useEffect(() => {
    const cell = linkedCell.current;
    if (cell === null || focusedFor.current === highlighted) return;
    focusedFor.current = highlighted;
    cell.scrollIntoView?.({ block: 'center' });
    cell.focus();
  }, [data, highlighted]);

  const reload = () => setReloadToken((n) => n + 1);

  /** `failureCode` → its catalog message, the raw code as fallback (FEED-003). */
  const failureLabel = (code: string | undefined) =>
    code === undefined
      ? t('resellerBilling.noFailureReason', 'No failure reason given')
      : t(`resellerBilling.failure.${code}`, FAILURE_FALLBACK[code] ?? code);

  const retry = async (charge: ResellerCharge) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setRetrying(charge.id);
    setProblem(null);
    setConfirmError(null);
    try {
      const updated = await adminApi.retryResellerCharge(resellerId, charge.id);
      if (updated.status === 'FAILED' || updated.status === 'REQUIRES_ACTION') {
        // The attempt was answered but didn't take (e.g. NO_CARD fails at once): act on it.
        setProblem(
          t('resellerBilling.retryFailed', "The retry didn't go through: {{failure}}", {
            failure: failureLabel(updated.failureCode),
          }),
        );
      } else {
        notify.success(
          t('resellerBilling.retried', 'Charge retried: {{status}}.', {
            status: t(`resellerBilling.chargeStatus.${updated.status}`, updated.status),
          }),
        );
      }
      setConfirming(null);
    } catch (err) {
      const code = err instanceof ApiError ? String(err.code) : '';
      if (isPayoutsMovedToLedger(err)) payoutActions.reload();
      setConfirmError(
        isPayoutsMovedToLedger(err) ? (
          <MovedToLedgerMessage ledgerTo={resellerLedgerPath(resellerId)} />
        ) : code === 'RESELLER_CHARGE_NOT_RETRYABLE' ? (
          t('resellerBilling.errors.notRetryable', "This charge can't be retried now.")
        ) : code === 'RESELLER_CHARGE_NOT_FOUND' ? (
          t(
            'resellerBilling.errors.notFound',
            "This charge couldn't be found. The list has been reloaded.",
          )
        ) : code === 'CONCURRENT_UPDATE' ? (
          t(
            'resellerBilling.errors.concurrent',
            'The charge was being updated at the same time. Try again.',
          )
        ) : (
          errorCopy(err, t, t('resellerBilling.errors.retry', "Couldn't retry the charge."))
        ),
      );
    } finally {
      inFlight.current = false;
      setRetrying(null);
      reload();
    }
  };

  const problemBanner =
    problem !== null ? (
      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setProblem(null)}>
        {problem}
      </Alert>
    ) : null;

  if (failure !== null && data === null) {
    // TMMPDM-0044: a retry's outcome outlives a re-read that fails after it.
    return (
      <Box>
        {problemBanner}
        <ErrorState
          error={failure}
          message={t('resellerBilling.loadError', 'Could not load billing.')}
          onRetry={reload}
        />
      </Box>
    );
  }

  if (data === null) return <LoadingSpinner />;

  const { card, charges } = data;
  const cardLabel =
    card?.last4 !== undefined
      ? `${card.brand ?? ''} •••• ${card.last4}`.trim()
      : t('resellerBilling.theCard', 'the card on file');

  return (
    <Box>
      {problemBanner}

      {view === 'card' ? (
        <>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
            {t('resellerBilling.cardTitle', 'Card on file')}
          </Typography>
          {card === null ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              {t(
                'resellerBilling.noCard',
                "No card on file. The reseller adds one in its console; until then deficits and shortfalls can't be charged.",
              )}
            </Alert>
          ) : (
            <Paper variant="outlined" sx={{ p: 2, mb: 3, maxWidth: 560 }}>
              <Table size="small" aria-label={t('resellerBilling.cardTitle', 'Card on file')}>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      {t('resellerBilling.brand', 'Brand')}
                    </TableCell>
                    <TableCell>{card.brand ?? '—'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      {t('resellerBilling.number', 'Number')}
                    </TableCell>
                    <TableCell>
                      {card.last4 !== undefined
                        ? t('resellerBilling.last4', '•••• {{last4}}', { last4: card.last4 })
                        : '—'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      {t('resellerBilling.expiry', 'Expires')}
                    </TableCell>
                    <TableCell>
                      {card.expMonth !== undefined && card.expYear !== undefined
                        ? t('resellerBilling.expiryValue', '{{mm}}/{{yyyy}}', {
                            mm: String(card.expMonth).padStart(2, '0'),
                            yyyy: String(card.expYear),
                          })
                        : '—'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      {t('resellerBilling.status', 'Status')}
                    </TableCell>
                    <TableCell>
                      <CardStatusChip status={card.status} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {t('resellerBilling.cardReadOnly', 'Read-only. The reseller manages its card.')}
              </Typography>
            </Paper>
          )}
        </>
      ) : (
        <>
          {frozen ? <FrozenHistoryBanner ledgerTo={resellerLedgerPath(resellerId)} /> : null}
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
            {t('resellerBilling.chargesTitle', 'Charges')}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label={t('resellerBilling.chargesTitle', 'Charges')}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('resellerBilling.col.period', 'Period')}</TableCell>
                  <TableCell>{t('resellerBilling.col.kind', 'Kind')}</TableCell>
                  <TableCell align="right">{t('resellerBilling.col.amount', 'Amount')}</TableCell>
                  <TableCell>{t('resellerBilling.col.status', 'Status')}</TableCell>
                  <TableCell align="right">{t('resellerBilling.col.attempt', 'Attempt')}</TableCell>
                  <TableCell>{t('resellerBilling.col.nextRetry', 'Next retry')}</TableCell>
                  <TableCell>{t('resellerBilling.col.holdsPayouts', 'Holds payouts')}</TableCell>
                  <TableCell>{t('resellerBilling.col.failure', 'Failure')}</TableCell>
                  <TableCell>{t('resellerBilling.col.actions', 'Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {charges.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 2 }}>
                      <EmptyState title={t('resellerBilling.noCharges', 'No charges.')} />
                    </TableCell>
                  </TableRow>
                ) : (
                  charges.map((charge) => {
                    const period = formatMonthYear(charge.periodStart, locale);
                    const linked = charge.id === highlighted;
                    return (
                      <TableRow key={charge.id} hover selected={linked} data-charge-id={charge.id}>
                        <TableCell
                          ref={linked ? linkedCell : null}
                          tabIndex={linked ? -1 : undefined}
                          sx={linked ? LINKED_CELL_SX : {}}
                        >
                          {period}
                          {linked ? (
                            <Box component="span" sx={visuallyHidden}>
                              {' '}
                              {t('resellerBilling.linkedCharge', 'Linked charge')}
                            </Box>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {t(
                            `resellerBilling.kind.${charge.kind}`,
                            CHARGE_KIND_FALLBACK[charge.kind],
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Amount
                            minor={charge.amountMinor}
                            currency={charge.currency}
                            locale={locale}
                          />
                        </TableCell>
                        <TableCell>
                          <ResellerChargeChip status={charge.status} />
                        </TableCell>
                        <TableCell align="right">
                          <Amount count={charge.attempt} locale={locale} />
                        </TableCell>
                        <TableCell>
                          {charge.nextRetryAt !== undefined
                            ? formatInstant(charge.nextRetryAt, locale)
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {charge.holdsPayouts ? (
                            <StatusChip
                              tone="warning"
                              label={t('resellerBilling.holdingPayouts', 'Holding payouts')}
                            />
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>
                          {charge.failureCode !== undefined
                            ? failureLabel(charge.failureCode)
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {!frozen && charge.retryable ? (
                            <SubmitButton
                              size="small"
                              variant="outlined"
                              busy={retrying === charge.id}
                              disabled={retrying !== null}
                              onClick={() => {
                                setConfirmError(null);
                                setConfirming(charge);
                              }}
                              aria-label={t(
                                'resellerBilling.retryAria',
                                'Retry the {{period}} charge',
                                { period },
                              )}
                            >
                              {t('resellerBilling.retry', 'Retry')}
                            </SubmitButton>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <ConfirmDialog
            open={confirming !== null}
            title={
              confirming
                ? t(
                    'resellerBilling.confirm.title',
                    'Retry charging {{amount}} to the card on file?',
                    {
                      amount: formatMoney(confirming.amountMinor, confirming.currency, locale),
                    },
                  )
                : ''
            }
            consequence={
              card === null
                ? t(
                    'resellerBilling.confirm.noCard',
                    'There is no card on file, so this attempt will fail at once.',
                  )
                : t('resellerBilling.confirm.body', 'The charge is sent to {{card}} now.', {
                    card: cardLabel,
                  })
            }
            confirmLabel={t('resellerBilling.retry', 'Retry')}
            busy={retrying !== null}
            error={confirmError}
            onConfirm={() => {
              if (confirming) void retry(confirming);
            }}
            onCancel={() => setConfirming(null)}
          />
        </>
      )}
    </Box>
  );
}

function CardStatusChip({ status }: { status: string }) {
  const { t } = useCopy();
  if (!Object.hasOwn(CARD_STATUS_FALLBACK, status)) {
    return <StatusChip label={status} />;
  }
  return (
    <StatusChip
      status={status}
      // An expired card needs the reseller to act, so it is a warning: the registry's `card` domain
      // (the shared EXPIRED is neutral — a §5 change request, see the TMMPDM-0065 delivery).
      domain="card"
      label={t(
        `resellerBilling.cardStatus.${status}`,
        CARD_STATUS_FALLBACK[status as ResellerBillingCardStatus],
      )}
    />
  );
}
